export const getYouTubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    // 1. Direct 11-character YouTube video ID match
    if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
        return trimmed;
    }
    // 2. Matches youtube.com/watch?v=..., youtu.be/..., youtube.com/embed/..., youtube.com/shorts/...
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = trimmed.match(regExp);
    if (match && match[2] && match[2].length === 11) {
        return match[2];
    }
    return null;
};

export const fetchYouTubeOEmbed = async (videoIdOrUrl) => {
    const videoId = getYouTubeVideoId(videoIdOrUrl);
    if (!videoId) return null;
    try {
        const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;
        const response = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(watchUrl)}&format=json`);
        if (!response.ok) return null;
        const data = await response.json();
        return data;
    } catch (err) {
        console.warn('Failed to fetch YouTube oEmbed:', err);
        return null;
    }
};

export const getSourceType = (source) => {
    if (!source || !source.type) return 'image';
    const typeStr = String(source.type).toLowerCase();
    if (typeStr.includes('youtube')) return 'youtube_video';
    return 'image';
};

export const getSourceUrl = (source) => {
    if (!source) return '';
    if (typeof source === 'string') return source;
    
    const meta = source.metadata || {};

    // 1. Direct top-level fields
    if (source.url) return source.url;
    if (source.src) return source.src;
    if (source.link) return source.link;
    if (source.content) return source.content;

    // 2. Metadata fields
    if (meta.url) return meta.url;
    if (meta.media && meta.media.url) return meta.media.url;

    // 3. Extract src from html iframe if present
    if (meta.html && typeof meta.html === 'string') {
        const srcMatch = meta.html.match(/src=["']([^"']+)["']/);
        if (srcMatch && srcMatch[1]) {
            return srcMatch[1];
        }
    }

    // 4. Fallback thumbnail url
    if (meta.thumbnail_url) return meta.thumbnail_url;
    
    return '';
};

export const getYouTubeEmbedUrl = (urlOrSource) => {
    if (!urlOrSource) return '';
    
    if (typeof urlOrSource === 'object' && urlOrSource !== null) {
        const meta = urlOrSource.metadata || {};
        if (meta.html && typeof meta.html === 'string') {
            const srcMatch = meta.html.match(/src=["']([^"']+)["']/);
            if (srcMatch && srcMatch[1]) {
                // If it already contains autoplay parameter, return as is or ensure autoplay
                const src = srcMatch[1];
                return src.includes('?') ? `${src}&autoplay=1` : `${src}?autoplay=1`;
            }
        }
    }

    const url = typeof urlOrSource === 'string' ? urlOrSource : getSourceUrl(urlOrSource);
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    return url;
};

export const getYouTubeThumbnail = (urlOrSource) => {
    if (!urlOrSource) return null;

    if (typeof urlOrSource === 'object' && urlOrSource !== null) {
        const meta = urlOrSource.metadata || {};
        if (meta.thumbnail_url) return meta.thumbnail_url;
        if (meta.media && meta.media.image) return meta.media.image;
    }

    const url = typeof urlOrSource === 'string' ? urlOrSource : getSourceUrl(urlOrSource);
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
        return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
    }
    return null;
};

export const getSourceTitle = (source) => {
    if (!source) return 'Untitled Source';
    const meta = source.metadata || {};

    return source.title ||
           source.name ||
           meta.title ||
           (meta.media && meta.media.title) ||
           meta.description ||
           (meta.media && meta.media.description) ||
           'Untitled Source';
};

export const getSourceDescription = (source) => {
    if (!source) return '';
    const meta = source.metadata || {};

    return source.description ||
           meta.description ||
           (meta.media && meta.media.description) ||
           '';
};

export const getSourceAuthorOrTopic = (source) => {
    if (!source) return '';
    const meta = source.metadata || {};

    return meta.author_name ||
           meta.provider_name ||
           meta.topic ||
           (meta.media && meta.media.siteName) ||
           '';
};
